import { Answer, Question } from '../types/domain';
import { RichTextAnswer } from './RichTextAnswer';

export function QuestionRenderer({ question, answer, onChange }: { question: Question; answer?: Answer; onChange: (value: Answer['value']) => void }) {
  if (question.type === 'long_answer') {
    return <RichTextAnswer value={(answer?.value as string | undefined) ?? ''} onChange={onChange} />;
  }

  if (question.type === 'case_study') {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-neon-blue/25 bg-neon-blue/10 p-4 text-sm leading-7 text-slate-200">
          <p className="font-semibold text-white">Structured prompts</p>
          <ol className="mt-3 list-decimal space-y-2 pl-5">
            {question.prompts?.map((prompt) => <li key={prompt}>{prompt}</li>)}
          </ol>
        </div>
        <textarea
          className="min-h-56 w-full rounded-lg border border-white/10 bg-slate-950/70 p-4 text-sm text-slate-100 outline-none focus:border-neon-blue"
          value={(answer?.value as string | undefined) ?? ''}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Write a structured response that addresses each prompt..."
        />
      </div>
    );
  }

  if (question.type === 'paragraph' || question.type === 'coding' || question.type === 'math') {
    return (
      <textarea
        className="min-h-48 w-full rounded-lg border border-white/10 bg-slate-950/70 p-4 font-mono text-sm text-slate-100 outline-none focus:border-neon-blue"
        value={(answer?.value as string | undefined) ?? ''}
        onChange={(event) => onChange(event.target.value)}
        placeholder={question.type === 'coding' ? 'Write your solution here...' : 'Type your answer...'}
      />
    );
  }

  if (question.type === 'true_false') {
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        {[true, false].map((value) => (
          <label key={String(value)} className="flex cursor-pointer items-center gap-3 rounded-lg border border-white/10 bg-white/[0.06] p-4 text-slate-100 transition hover:border-neon-violet">
            <input type="radio" checked={answer?.value === value} onChange={() => onChange(value)} />
            <span>{value ? 'True' : 'False'}</span>
          </label>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {question.options?.map((option) => {
        const isCheckbox = question.type === 'checkbox';
        const checked = isCheckbox ? Array.isArray(answer?.value) && answer.value.includes(option) : answer?.value === option;
        return (
          <label key={option} className="flex cursor-pointer items-center gap-3 rounded-lg border border-white/10 bg-white/[0.06] p-4 text-slate-100 transition hover:border-neon-violet hover:bg-neon-violet/10">
            <input
              type={isCheckbox ? 'checkbox' : 'radio'}
              checked={checked}
              onChange={() => {
                if (!isCheckbox) onChange(option);
                else {
                  const current = Array.isArray(answer?.value) ? answer.value : [];
                  onChange(checked ? current.filter((item) => item !== option) : [...current, option]);
                }
              }}
            />
            <span>{option}</span>
          </label>
        );
      })}
    </div>
  );
}
